// Vue 3 migration Phase 5a: vue-router@4 dropped the global `Vue.use(Router)`
// install. The router is created via `createRouter` + `createWebHistory()`
// (replaces the v3 `mode: 'history'` option) and registered on the app via
// `app.use(router)` (see main.js bootstrap).
import { createRouter, createWebHistory } from 'vue-router'
import { maybeReloadForStaleChunk } from '@/utils/staleChunk'

/*
 *  SUBROUTES
*/
import adminRoutes from '@/router/subroutes/adminRoutes'
import studentAdministrationRoutes from '@/router/subroutes/studentAdministrationRoutes'
import groupRoutes from '@/router/subroutes/groupRoutes'
import profileRoutes from '@/router/subroutes/profileRoutes'


/*
 *   VIEWS
*/
const fourOThree = () => import('@/views/403')
const FourOFour = () => import('@/views/404')
const adminMainView = () => import('@/views/admin/adminMainView')
const conversationsView = () => import('@/views/messages/messagesView')
const dashboard = () => import(/* webpackPrefetch: true */'@/views/dashboard')
const assignmentsMissingFeedback = () => import('@/views/assignments/assignmentsMissingFeedbackView')
const frontpage = () => import('@/views/frontpage/frontpage')
const groupsMainView = () => import('@/views/groups/groupsMainView')
const groupsOverview = () => import(/* webpackPrefetch: true */'@/views/groups/groupsOverview')
const institutionFilesharingView = () => import('@/views/files/institutionFilesharingView')
const institutionNewsView = () => import('@/views/news/institutionNewsView.vue')
const videoChatNew = () => import('@/views/videoChat/videoChat.vue')
const consultationVideoCall = () => import('@/views/consultation/consultationVideoCall.vue')
const inviteToInstitutionView = () => import('@/views/invite/inviteToInstitutionView')
const joinView = () => import(/* webpackPrefetch: true */'@/views/join/joinView')
const login = () => import(/* webpackPrefetch: true */'@/views/login/loginView')
const moodlelogin = () => import('@/views/login/moodleloginView')
const profileMainView = () => import('@/views/profile/profileMainView')
const redirectPage = ()=>import('@/views/redirectPage/redirectPage')
const resetPasswordView = () => import('@/views/login/resetPasswordView')
const searchMainView = () => import('@/views/search/searchMainView')
const signupConfirmView = () => import('@/views/sign-up/signupConfirmView')
const signupView = () => import(/* webpackPrefetch: true */'@/views/sign-up/signupView')
const sharedOverview = () => import('@/views/shared/sharedOverview')
const studentAdministrationOverview = () => import('@/views/studentadministration/studentAdministrationOverview')
const testView = () => import('@/views/testView')
const assignmentFeedbackMediaView = () => import(/* webpackPrefetch: true */'@/views/assignments/assignmentFeedbackMediaView')

// application routes
const routes = [
    // Agent test harness — stg only (skipped entirely in prod bundle)
    ...((import.meta.env.MODE !== 'production') ? [{
        path: '/agent-test',
        component: () => import('@/views/agent/AgentTestPage.vue'),
        meta: { view: 'agent-test', requiresAuth: true },
    }] : []),

    {
        path: '/',
        component: frontpage,
        meta: { view : 'frontpage' }
    },
    {
        path: '/403',
        component: fourOThree,
        meta : { view : '403' }
    },
    {
        path: '/404',
        component: FourOFour,
        meta : { view : '404' }
    },
    {
        path: '/admin',
        component: adminMainView,
        children: adminRoutes,
        meta : { view : 'admin-main' }
    },
    {
        path: '/administration',
        component: studentAdministrationOverview,
        meta: { requiresAuth: true, view : 'student-administration-main' },
        children : studentAdministrationRoutes
    },
    {
        path: '/dashboard',
        component: dashboard,
        meta: { requiresAuth: true, view : 'dashboard' }
    },
    {
        path: '/assignments/feedback/missing',
        component: assignmentsMissingFeedback,
        meta: { requiresAuth: true, view : 'assignments-missing-feedback' }
    },
    {
        path: '/email-redirect-bot',
        component: redirectPage,
        meta: { requiresAuth: true, view : 'redirect-page' }
    },
    {
        path: '/files',
        component: institutionFilesharingView,
        meta: { requiresAuth: true, view : 'institution-files' }
    },
    {
        path: '/groups',
        component: groupsOverview,
        meta: { requiresAuth: true, view: 'groups-overview' },
    },
    {
        path: '/groups/:group_id',
        component: groupsMainView,
        meta: { requiresAuth: true, view : 'groups-main' },
        children: groupRoutes
    },
    {
        path: '/groups/:group_id/assignment/:assignment_id/feedback/:feedback_id?/media/:media_id/view',
        component: assignmentFeedbackMediaView,
        props: route => ({
            group_id : parseInt(route.params.group_id),
            assignment_id : parseInt(route.params.assignment_id),
            feedback_id : route.params.feedback_id ? parseInt(route.params.feedback_id) : undefined,
            media_id : parseInt(route.params.media_id)
        }),
        meta: { requiresAuth: true, view : 'groups-assignment-feedback-media-view' },
    },
    {
        path: '/institution/invite',
        component: inviteToInstitutionView,
        meta: {
            requiresAuth: true,
            view: 'invite-to-institution',
        },
    },
    {
        path: '/join/:join_id?',
        component: joinView,
        meta: { view: 'join-view' },
    },
    {
        path: '/login/:error',
        component: login,
        meta : { view : 'login-error' }
    },
    {
        path: '/login',
        component: login,
        meta : { view : 'login' }
    },
    {
        path: '/messages/:conversation_id',
        component: conversationsView,
        meta: { requiresAuth: true, view : 'messages' },
    },
    {
        path: '/messages',
        component: conversationsView,
        meta: { requiresAuth: true, view : 'messages' },
    },
    {
        path: '/moodle-login/:token/:course_id',
        component: moodlelogin,
        meta : { view : 'moodle-login' }
    },
    {
        path: '/news',
        component: institutionNewsView,
        meta: { requiresAuth: true, view : 'institution-news' }
    },
    {
        path: '/consultation-video/:consultationId',
        component: consultationVideoCall,
        meta: { requiresAuth: true, view: 'consultation-video' }
    },
    {
        path: '/videochat/:id',
        component: videoChatNew,
        meta: { requiresAuth: true, view : 'learnu' }
    },
    {
        path: '/profile/:user_id',
        component: profileMainView,
        meta: { requiresAuth: true, view : 'profile-main' },
        children: profileRoutes
    },
    {
        path: '/reset-password/:token',
        component: resetPasswordView,
        meta: { view : 'reset-password' },
    },
    {
        path: '/search',
        component: searchMainView,
        meta: { requiresAuth: true, view : 'search' },
    },
    {
        path: '/signup',
        component: signupView,
        meta : { view : 'signup' }
    },
    {
        path: '/signup/:type',
        component: signupView,
        meta : { view : 'signup' }
    },
    {
        path: '/signup/confirm/:token',
        component: signupConfirmView,
        meta : { view : 'signup confirm' }
    },
    {
        path: '/shared-with-me',
        component: sharedOverview,
        meta: { requiresAuth: true, view: 'shared-overview' },
    },
    {
        path: '/test',
        component: testView,
        meta: { requiresAuth: true }
    },
    // every other non-existing route go to 404
    // vue-router@4: the v3 `path: '*'` catch-all is now `/:pathMatch(.*)*`.
    {
        path: '/:pathMatch(.*)*',
        redirect: '/404',
    },
]

// export router instance
const router = createRouter({
    history: createWebHistory(),
    routes,
    linkActiveClass: 'active',
    linkExactActiveClass: 'exact-active',
})

/**
 * Auto-recover from stale-chunk errors after a deploy — the navigation-time
 * case, i.e. a lazy ROUTE whose chunk no longer exists.
 *
 * Vite emits each lazy-loaded route as `assets/<chunk>-<contenthash>.js`.
 * The `index.html` served at app boot pins the user's session to a specific
 * set of hashes; a new deploy changes them, the host's SPA fallback rewrites
 * the missing file to `index.html`, and the browser refuses to evaluate HTML
 * as a JS module. Without intervention the page is broken until the user
 * hard-refreshes.
 *
 * Matching and the reload guard live in utils/staleChunk.js, shared with
 * main.js's window listeners and its app.config.errorHandler (the lazy
 * COMPONENT case) so all three paths honour one 30s guard between them and a
 * genuinely-broken bundle can't trap the tab in a reload loop.
 */
router.onError((err) => {
    maybeReloadForStaleChunk(err)
})

export default router
